import {
  Alert,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthApi } from '@api';
import { SettingsConfig } from './SettingsAccordionConfig';
import { AppContext } from '@/contexts/AppContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { getStoredPersona, setPersona } from '@/utils/persona';
import { isChildModeEnabled, setChildMode } from '@/utils/childMode';
import type { Persona } from '@/personas/types';

/**
 * Persona-väljare (NPF) som ersätter barnläges-toggeln. En ToggleButtonGroup med
 * tre vylägen: child/teen/adult.
 *
 * R1-constraint: väljaren SKRIVER bara flaggor, den RESOLVAR aldrig en profil —
 * den anropar alltså ALDRIG useActivePersona/resolveActiveProfile. Reaktivt
 * temabyte sker via setPersona/setChildMode som dispatchar 'storage' (App.tsx +
 * useActivePersona lyssnar).
 *
 * Förvald knapp härleds lokalt utan att resolva: getStoredPersona(tag) om satt,
 * annars child om childMode redan är på, annars adult.
 *
 * SJÄLVDÖLJANDE för barn och tonåring SOM HAR EN HANDLEDARE (samma mönster som
 * ChangeCharacterSetting, som döljer sig när childMode är av). Profilen ägs då av
 * handledaren — ett barn som kunde göra sig till vuxen hade slagit av barnvyn åt sig
 * själv, och belöningarna med den. Handledaren sätter den i stället i
 * SupervisorPersona ('/supervisor/persona'), som skriver brukarens konto via
 * PUT /user/persona med supervisedTag. Den VUXNA brukaren behåller sin väljare: hen
 * styr sitt eget användande.
 *
 * Villkoret "har en handledare" är inte kosmetiskt utan spärren mot en enkelriktad
 * dörr. Ett konto UTAN handledare som satt sig till barn eller tonåring har ingen
 * annan som kan ändra tillbaka, och sedan vägen till vuxenläget togs bort ur
 * brukarvyn finns ingen nödutgång. Därför: finns ingen handledare får kontot
 * fortfarande välja själv.
 *
 * Gaten läser kontots persona (user.persona, serverns värde) och inte den lokala
 * spegeln — spegeln är just det ett barn kan ändra. Saknas serverns värde (äldre
 * konto, ännu ej läst) visas väljaren: en vuxen utan satt profil måste kunna sätta
 * den, och en handledare kan alltid skriva över.
 *
 * Mappning val → flaggor: persona skrivs FÖRST, childMode SIST.
 *   child/teen → childMode=true (barnvyn gateas på childMode)
 *   adult → childMode=false
 */
const deriveDefaultPersona = (): Persona => (isChildModeEnabled() ? 'child' : 'adult');

const PERSONAS: readonly Persona[] = ['child', 'teen', 'adult'];

const PersonaSelector = (props: SettingsConfig) => {
  const { t } = useTranslation();
  const { user, setUser } = useContext(AppContext);
  const { isSupervising, setSupervising, supervisors } = useContext(SupervisorContext);
  const tag = user?.tag;
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const [selected, setSelected] = useState<Persona>(
    () => getStoredPersona(tag) ?? deriveDefaultPersona(),
  );

  // Speglar useActivePersona: håll markerad knapp i synk när persona/childMode
  // ändras från annan källa (annan flik eller handledare byter klient medan
  // panelen är monterad). deriveDefaultPersona är modul-nivå och läser bara
  // global childMode, så [tag] är rätt dep — endast tag och setSelected används.
  useEffect(() => {
    const onStorage = () => setSelected(getStoredPersona(tag) ?? deriveDefaultPersona());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [tag]);

  // Vyn ändras direkt; serverns svar behåller den auktoritativa godkännandepolicyn.
  // Ett pågående anrop slutförs före nästa val så svaren inte kan komma i fel ordning.
  const persistPersona = async (next: Persona) => {
    setSaving(true);
    setSaveFailed(false);
    try {
      const updatedUser = await new AuthApi().updatePersona(next);
      setUser(updatedUser);
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (_: React.MouseEvent<HTMLElement>, next: Persona | null) => {
    if (!next || saving) return; // ToggleButtonGroup ger null vid avklick — ignorera
    setSelected(next);
    setPersona(tag, next); // persona FÖRST (lokal spegel + 'storage'-event)
    setChildMode(next === 'child' || next === 'teen'); // childMode SIST
    // Gör valet AUKTORITATIVT. effectiveChildMode = childMode && !currentClient &&
    // !isSupervising — så utan detta kunde barn/teen väljas medan handledarläget låg
    // kvar och tyst höll vyn i vuxenläge: knappen såg vald ut men ingenting hände.
    // Går via setSupervising (ALDRIG genom att skriva 'supervising' direkt) så att
    // currentClient rensas och 'storage' dispatchas — se docblocken i useRole.tsx.
    if (next !== 'adult' && isSupervising) setSupervising(false);
    void persistPersona(next); // backend SIST — lokal state har redan uppdaterats
  };

  // Barn och tonåring MED handledare får inte ändra sin egen profil — se docblocken
  // överst, särskilt om varför handledaren måste finnas för att valet ska låsas.
  const ownedBySupervisor =
    (user?.persona === 'child' || user?.persona === 'teen') && (supervisors?.length ?? 0) > 0;
  if (ownedBySupervisor) {
    return (
      <Box sx={{ p: 2, width: '100%' }}>
        <Typography variant="h6" color={'primary'}>
          {t(props.title)}
        </Typography>
        <Typography variant="caption" color={'text.secondary'}>
          {t('persona.settings.supervisorOnly')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: 'flex', width: '100%' }}>
      <Stack direction={'column'} width={'100%'} spacing={1}>
        <Typography variant="h6" color={'primary'}>
          {t(props.title)}
        </Typography>
        <ToggleButtonGroup
          exclusive
          disabled={saving}
          value={selected}
          onChange={handleChange}
          aria-label={t('persona.settings.ariaGroup') ?? ''}
          fullWidth
        >
          {PERSONAS.map((persona) => (
            <ToggleButton key={persona} value={persona}>
              {t(`persona.settings.${persona}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {saveFailed && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                onClick={() => void persistPersona(selected)}
                disabled={saving}
              >
                {t('Try again')}
              </Button>
            }
          >
            {t('persona.settings.saveError')}
          </Alert>
        )}
        <Typography variant="caption" color={'text.secondary'}>
          {t('persona.settings.help')}
        </Typography>
      </Stack>
    </Box>
  );
};

export default PersonaSelector;
